import React, { useEffect, useState } from 'react';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Constants
import { MAX_CARD_NAME_LENGTH, STRINGS } from '../../constants';

interface NewCardDialogProps {
  isOpen: boolean;
  /** True while the create request is in flight: both buttons disable, the
   *  confirm reads "Creating…", and closing is ignored until it settles. */
  isSubmitting?: boolean;
  /**
   * The server's refusal of the name just submitted — a duplicate, in practice.
   * Rendered under the field rather than as a toast: the message is about that
   * box, and the dialog stays open with the name still in it so the fix is one
   * edit away.
   */
  error?: string | null;
  /** Fires on every keystroke so the owner can drop a message the name has
   *  outlived. */
  onNameChange?: () => void;
  onCancel: () => void;
  onCreate: (name: string) => void;
}

/**
 * "+ New Card" asks for a name first, then creates the card and opens its editor
 * (spec R22). Create stays disabled until something has been typed, so a card
 * can never be created nameless.
 */
const NewCardDialog = ({
  isOpen,
  isSubmitting = false,
  error = null,
  onNameChange,
  onCancel,
  onCreate,
}: NewCardDialogProps) => {
  const [name, setName] = useState('');

  const handleNameChange = (next: string) => {
    setName(next);
    onNameChange?.();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setName('');
    }
  }, [isOpen]);

  const trimmed = name.trim();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={STRINGS.newCardDialog.title}
      size="md"
      footer={
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button variant="tertiary" showIcon={false} disabled={isSubmitting} onClick={handleClose}>
            {STRINGS.newCardDialog.cancel}
          </Button>
          <Button
            variant="primary"
            showIcon={false}
            disabled={trimmed === '' || isSubmitting}
            onClick={() => onCreate(trimmed)}
          >
            {isSubmitting ? STRINGS.newCardDialog.creating : STRINGS.newCardDialog.confirm}
          </Button>
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Typography variant="body-primary" as="p" className="tw-text-ink-secondary">
          {STRINGS.newCardDialog.description}
        </Typography>
        <TextField
          label={STRINGS.newCardDialog.nameLabel}
          placeholder={STRINGS.newCardDialog.namePlaceholder}
          value={name}
          onChange={handleNameChange}
          maxLength={MAX_CARD_NAME_LENGTH}
          required
          error={error !== null}
          helperText={error ?? ''}
        />
      </div>
    </Dialog>
  );
};

export default NewCardDialog;
